import { TableBody, TableContainer, TableRow } from "@mui/material";
import {
	ETAText,
	ItemAffectedText,
	PartOnBackorderText,
	QuantityFullText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { models } from "types/api/viewModels";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import { Fragment, useMemo } from "react";
import { BackorderPartsCellContent } from "components/OrderDetailsLineItem/OrderDetailsOnBackorderAccordionGrid.styles.ts";
import { JSX } from "@emotion/react/jsx-runtime";

interface AccordionGridProps {
	orderDetail: models["OrderDetailViewModel"] | null;
	parts?: models["PartViewModel"][] | null;
	sku?: string | null;
	itemDescription?: string | null;
	quantityBackordered?: number | null;
	partsQuantityBackordered?: number | null;
}

const OrderDetailsOnBackorderAccordionGrid = ({
	orderDetail,
	parts,
	sku,
	itemDescription,
	quantityBackordered,
	partsQuantityBackordered
}: AccordionGridProps) => {
	const eta = orderDetail?.eta;
	const backorderedPartsTableHeadCells = useMemo(
		() =>
			[
				{ id: "partsOnBackorder", label: PartOnBackorderText },
				{ id: "itemAffected", label: ItemAffectedText },
				partsQuantityBackordered !== 0 || quantityBackordered !== 0
					? { id: "quantity", label: QuantityFullText }
					: undefined,
				!partsQuantityBackordered && quantityBackordered === 0 ? { id: "eta", label: ETAText } : undefined
			]
				.filter((cell): cell is { id: string; label: string } => cell !== undefined)
				.map((cell) => ({
					...cell,
					disablePadding: false,
					numeric: false,
					sortable: false
				})) satisfies HeadCell[],
		[partsQuantityBackordered, quantityBackordered]
	);

	const getPartsTableRows = (part: models["PartViewModel"]) => {
		const quoteTableCells = [
			{
				testId: "parts-table-part",
				content: (
					<BackorderPartsCellContent>
						<span>{part.number ?? UnavailableDataPlaceholderText}</span>
						<span>{part.description ?? UnavailableDataPlaceholderText}</span>
					</BackorderPartsCellContent>
				)
			},
			{
				testId: "parts-table-itemAffected",
				content: (
					<BackorderPartsCellContent>
						<span>{sku}</span>
						<span>{itemDescription ?? UnavailableDataPlaceholderText}</span>
					</BackorderPartsCellContent>
				)
			},
			partsQuantityBackordered !== 0 || quantityBackordered !== 0
				? {
						testId: "parts-table-quantity",
						content: (
							<BackorderPartsCellContent>
								<span>{part.quantity}</span>
							</BackorderPartsCellContent>
						)
					}
				: undefined,
			!partsQuantityBackordered && quantityBackordered === 0
				? {
						testId: "parts-table-part-eta",
						content: (
							<BackorderPartsCellContent>
								<span>{eta ?? UnavailableDataPlaceholderText}</span>
							</BackorderPartsCellContent>
						)
					}
				: undefined
		].filter((cell): cell is { testId: string; content: JSX.Element } => cell !== undefined);

		return (
			<Fragment>
				{quoteTableCells.map(({ testId, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
					>
						{content}
					</StyledTableCell>
				))}
			</Fragment>
		);
	};

	return (
		<TableContainer sx={{ overflowX: "auto" }}>
			<StyledTableWrapper
				data-testid="backorder-table"
				aria-label="Backorder Table"
			>
				<SortableTableHead
					onRequestSort={() => {}}
					headCells={backorderedPartsTableHeadCells}
				/>
				<TableBody>
					{partsQuantityBackordered !== 0 && parts && (
						<>{parts?.map((part) => <TableRow key={part.number}>{getPartsTableRows(part)}</TableRow>)}</>
					)}
					{quantityBackordered !== 0 && (
						<TableRow>
							<StyledTableCell>
								<BackorderPartsCellContent>
									<span>{sku ?? UnavailableDataPlaceholderText}</span>
									<span>{itemDescription ?? UnavailableDataPlaceholderText}</span>
								</BackorderPartsCellContent>
							</StyledTableCell>
							<StyledTableCell>
								<BackorderPartsCellContent>
									<span>{sku ?? UnavailableDataPlaceholderText}</span>
									<span>{itemDescription ?? UnavailableDataPlaceholderText}</span>
								</BackorderPartsCellContent>
							</StyledTableCell>
							{(partsQuantityBackordered !== 0 || quantityBackordered !== 0) && (
								<StyledTableCell>
									<BackorderPartsCellContent>
										<span>{quantityBackordered ?? UnavailableDataPlaceholderText}</span>
									</BackorderPartsCellContent>
								</StyledTableCell>
							)}
							{!partsQuantityBackordered && quantityBackordered === 0 && (
								<StyledTableCell>
									<BackorderPartsCellContent>
										<span>{UnavailableDataPlaceholderText}</span>
									</BackorderPartsCellContent>
								</StyledTableCell>
							)}
						</TableRow>
					)}
				</TableBody>
			</StyledTableWrapper>
		</TableContainer>
	);
};

export default OrderDetailsOnBackorderAccordionGrid;
